import { useEffect, useMemo, useState } from "react";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { getAllObjectsLayer } from "../../../../widgets/map/mapFeatures/layers";
import MapWidget from "../../../../widgets/map/mapWidget/MapWidget";
import { MapOverlayWidget } from "../../../../widgets/map/mapAddons/mapOverlayWidget";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";
import { useGetRepairPlansQuery } from "../../../../api/rtk/base/slices/objectTypes/repairPlanApiSlice";
import { useMapURLParams } from "../../../../hooks/map/useMapParams";
import { useMapSlice } from "../../../../stateManagement/hooks/useMapSlice";
import { MapName } from "../../../../stateManagement/reducers/maps/actions";
import { useRepairPlanFilters } from "./useRepairPlanFilters";
import { useRepairPlanLayers } from "./useRepairPlanLayers";
import { showMapTooltip } from "src/widgets/map/mapFeatures/mapFunctions/showMapTooltip/showMapTooltip";
import { useDispatch } from "react-redux";
import { setMapTooltipIsVisible } from "src/stateManagement/reducers";
import { getObjectsOnWMSLayer } from "src/widgets/map/mapFeatures/mapFunctions/getObjectsOnMapPoint/getObjectsOnMapPoint";
import { MapTooltip } from "src/widgets/map/mapAddons/mapTooltip";
import TableUnderMap from "src/pages/tables/tableUnderMap/TableUnderMap";
import { useTableUnderMapFilters } from "src/pages/tables/tableUnderMap/useTableUnderMapFilters";


export function MapRepairPlan() {
    const dispatch = useDispatch();
    const { mapName } = useMapURLParams() as { mapName: MapName; mapOption?: string };
    const { tableUnderMapIsShown } = useAppSelector(state => state.contentHeader);

    const [newestRepairPlan, setNewestRepairPlan] = useState<any>();
    const [tableFilters, setTableFilters] = useState<any>({});
    const [objectTypes, setObjectTypes] = useState<string | string[]>();
    const [permanentLayers] = useState([getAllObjectsLayer()]);

    const mapState = useMapSlice(mapName);
    const { mapFiltersExpressions, tableUnderMapFiltersExpressions }: any = mapState ?? {};

    const currentConfig = useMemo(() => getItemWebStorage('currentConfig'), []);
    const { menuAdditionalItems } = currentConfig ?? {};
    const menuItem = useMemo(
        () => menuAdditionalItems?.find((menuItem: any) => menuItem?.item?.type === `map.${mapName}`)?.item,
        [menuAdditionalItems, mapName]
    );

    const { data: repairPlans } = useGetRepairPlansQuery({ $orderby: "Year desc", $filter: "(Accepted eq 1)" });

    const { mergedMapFilters, thresholdsFilter } = useRepairPlanFilters(newestRepairPlan);
    const hasRepairPlan = Boolean(newestRepairPlan);
    const additionalLayers = useRepairPlanLayers(
        hasRepairPlan,
        menuItem,
        mergedMapFilters,
        mapFiltersExpressions,
        currentConfig,
        thresholdsFilter
    );

    // Обработка данных планов
    useEffect(() => {
        if (Array.isArray(repairPlans?.value) && repairPlans.value.length > 0) {
            setNewestRepairPlan(repairPlans.value[0]);
        }
    }, [repairPlans]);

    // Настройка табличных фильтров
    const { objectTypes: calcObjectTypes, tableFilters: calcTableFilters } = useTableUnderMapFilters({
        menuItem,
        appliedFilters: mergedMapFilters,
        baseFiltersExpressions: tableUnderMapFiltersExpressions,
        forcedFiltersExpressions: useMemo<Record<string, string> | undefined>(() => {
            if (newestRepairPlan?.RepairPlanID != null) {
                return { RepairPlanId: `RepairPlanId eq ${newestRepairPlan?.RepairPlanID}` };
            }
            return undefined;
        }, [newestRepairPlan]),
        defaultObjectType: 'RepairSegments'
    });

    useEffect(() => {
        setObjectTypes(calcObjectTypes);
        setTableFilters(calcTableFilters);
    }, [calcObjectTypes, calcTableFilters]);

    function applyMapTooltip(map: any) {
        const pointerMoveFunc = (event: any) => showMapTooltip(event, mapName, additionalLayers[0], map)
        map?.on("pointermove", pointerMoveFunc)
        return {
            off: () => {
                map?.un("pointermove", pointerMoveFunc)
                dispatch(setMapTooltipIsVisible(false))
            }
        }
    }

    function applyMapObjectsFind(map: any) {
        const clickFunc = (event: any) => getObjectsOnWMSLayer(event, mapName, additionalLayers[0])
        map?.on("singleclick", clickFunc)
        return {
            off: () => {
                map?.un("singleclick", clickFunc)
            }
        }
    }

    const mapEventHandlers = [
        {
            name: "mapTooltip",
            func: applyMapTooltip
        },
        {
            name: "objectsFind",
            func: applyMapObjectsFind
        }
    ]

    return (
        <>
            <ContentHeader
                title={'Опорный план ремонтных работ' + (newestRepairPlan?.Year ? ` - ${newestRepairPlan?.Year}` : '')}
                objectTypeName={mapName}
                type="map"
                option="repairPlan"
            />
            <div className="map-widget-with-toast">
                <MapTooltip type={mapName} />
                <MapWidget
                    additionalLayers={additionalLayers}
                    permanentLayers={permanentLayers}
                    mapEventHandlers={mapEventHandlers}
                    mapSizeClass={tableUnderMapIsShown ? 'half-size-map-widget' : null}
                />
                <MapOverlayWidget />
                {tableUnderMapIsShown && (
                    <TableUnderMap objectTypes={objectTypes} canBeFiltered={false} filters={tableFilters} />
                )}
            </div>
        </>
    )
}
